import React from "react";
import {
  Badge,
  Box,
  Drawer,
  GlobalStyles,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import HomeIcon from "@mui/icons-material/Home";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ChatIcon from "@mui/icons-material/Chat";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../firebase";
import { endSession } from "../session";
import { brand } from "../theme";
import Logo from "./Logo";
import { useChatNotifications } from "./ChatNotifications";
import { useUserProfile } from "./UserProfile";

export const SIDEBAR_WIDTH = 248;

const HIDDEN_PATHS = ["/login", "/register"];

const navItems = [
  { to: "/home", icon: <HomeIcon />, label: "Inicio" },
  { to: "/publish", icon: <AddCircleIcon />, label: "Publicar" },
  { to: "/myCars", icon: <DirectionsCarIcon />, label: "Mis vehículos" },
  { to: "/chats", icon: <ChatIcon />, label: "Chats" },
  { to: "/profile", icon: <AccountCircleIcon />, label: "Perfil" },
  { to: "/support", icon: <SupportAgentIcon />, label: "Soporte" },
];

const isActivePath = (pathname, to) =>
  pathname === to ||
  (to === "/home" && (pathname === "/" || pathname.startsWith("/ver"))) ||
  (to === "/chats" && pathname.startsWith("/chatbox"));

const itemSx = (active) => ({
  borderRadius: 2,
  mb: 0.5,
  py: 1.1,
  color: active ? brand.cream : alpha(brand.cream, 0.65),
  bgcolor: active ? alpha(brand.coral, 0.16) : "transparent",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 10,
    bottom: 10,
    width: 3,
    borderRadius: 2,
    bgcolor: active ? brand.coral : "transparent",
  },
  "& .MuiListItemIcon-root": {
    color: active ? brand.coral : "inherit",
    minWidth: 40,
  },
  "&:hover": {
    bgcolor: active ? alpha(brand.coral, 0.22) : alpha(brand.cream, 0.08),
    color: brand.cream,
  },
});

// Barra lateral para escritorio; en móvil se usa BottomNav
const SideNav = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [user] = useAuthState(auth);
  const { totalUnread } = useChatNotifications();
  const { isComplete } = useUserProfile();
  const location = useLocation();
  const navigate = useNavigate();

  if (!isDesktop || !user || !user.emailVerified || !isComplete || HIDDEN_PATHS.includes(location.pathname)) {
    return null;
  }

  const handleLogout = async () => {
    await endSession();
    navigate("/login");
  };

  return (
    <>
      {/* Desplaza el contenido y las barras superiores fijas para dejar lugar a la sidebar */}
      <GlobalStyles
        styles={{
          body: { paddingLeft: SIDEBAR_WIDTH },
          ".MuiAppBar-positionFixed, .MuiAppBar-positionAbsolute": {
            left: `${SIDEBAR_WIDTH}px !important`,
            right: 0,
            width: "auto !important",
          },
        }}
      />
      <Drawer
        variant="permanent"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
            bgcolor: brand.ink,
            color: brand.cream,
            borderRight: "none",
            px: 2,
            py: 3,
          },
        }}
      >
        <Box
          component={Link}
          to="/home"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 1,
            mb: 4,
            color: "inherit",
            textDecoration: "none",
          }}
        >
          <Logo size={40} sx={{ "& rect": { fill: brand.inkLight } }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1, letterSpacing: "-0.02em" }}>
              Caro
            </Typography>
            <Typography sx={{ fontSize: 11, opacity: 0.55, mt: 0.5 }}>
              Compra y venta de autos
            </Typography>
          </Box>
        </Box>

        <List disablePadding sx={{ flexGrow: 1 }}>
          {navItems.map((item) => {
            const active = isActivePath(location.pathname, item.to);
            return (
              <ListItemButton
                key={item.to}
                component={Link}
                to={item.to}
                sx={itemSx(active)}
              >
                <ListItemIcon>
                  {item.to === "/chats" ? (
                    <Badge badgeContent={totalUnread} color="secondary" max={99}>
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: active ? 700 : 500, fontSize: 15 }}
                />
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ borderTop: `1px solid ${alpha(brand.cream, 0.12)}`, pt: 2 }}>
          {user.email && (
            <Typography
              noWrap
              title={user.email}
              sx={{ fontSize: 12, opacity: 0.55, px: 1, mb: 1 }}
            >
              {user.email}
            </Typography>
          )}
          <ListItemButton onClick={handleLogout} sx={itemSx(false)}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Cerrar sesión"
              primaryTypographyProps={{ fontWeight: 500, fontSize: 15 }}
            />
          </ListItemButton>
        </Box>
      </Drawer>
    </>
  );
};

export default SideNav;
